import { useState, useEffect, useRef } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { TaskDetailModal } from '../components/TaskDetailModal';
import {
  LIST_TASKS_BY_CASE,
  CREATE_TASK,
  LIST_TASK_TEMPLATES,
} from '../graphql/tasks';
import { GET_INQUIRIES } from '../graphql/inquiries';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface Case {
  id: string;
  caseNumber: string;
  clientId: string;
  caseType?: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  status: string;
}

interface Inquiry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  incidentType: string;
  status: string;
  caseId?: string;
  userId?: string;
}

interface ListInquiriesData {
  listInquiries: {
    items: Inquiry[];
  };
}

interface ListCasesData {
  listCases: {
    items: Case[];
  };
}

interface TaskTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  taskType: string;
  priority: string;
  defaultDueDays: number;
  category: string;
}

interface ListTaskTemplatesData {
  listTaskTemplates: TaskTemplate[];
}

interface Task {
  id: string;
  caseId: string;
  clientId: string;
  title: string;
  description: string;
  taskType: string;
  priority: string;
  status: string;
  dueDate: string;
  completedAt?: string;
  assignedByName?: string;
}

interface ListTasksByCaseData {
  listTasksByCase: {
    items: Task[];
  };
}

interface CreateTaskData {
  createTask: Task;
}

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_CASES = gql`
  query ListCases {
    listCases {
      items {
        id
        caseNumber
        clientId
        caseType
        client {
          id
          firstName
          lastName
        }
        status
      }
    }
  }
`;

export function TasksList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<string>('ALL');
  const [clientFilter, setClientFilter] = useState<string>('ALL');
  const [caseFilter, setCaseFilter] = useState<string>('ALL');
  const [allTasks, setAllTasks] = useState<any[]>([]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);

  // Task form state
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskType, setTaskType] = useState('DOCUMENT_UPLOAD');
  const [taskPriority, setTaskPriority] = useState('MEDIUM');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskStaffNotes, setTaskStaffNotes] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { data: userData, loading: userLoading } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: casesData, loading: casesLoading } = useQuery<ListCasesData>(LIST_CASES);
  const { data: inquiriesData, loading: inquiriesLoading } = useQuery<ListInquiriesData>(GET_INQUIRIES, {
    variables: { limit: 500 },
  });
  const { data: templatesData } = useQuery<ListTaskTemplatesData>(LIST_TASK_TEMPLATES);
  const [getTasksByCase] = useLazyQuery<ListTasksByCaseData>(LIST_TASKS_BY_CASE);
  const [createTask] = useMutation<CreateTaskData>(CREATE_TASK);

  const user = userData?.getCurrentUser;
  const cases = casesData?.listCases?.items || [];
  const inquiries = inquiriesData?.listInquiries?.items || [];
  const templates = templatesData?.listTaskTemplates || [];

  // State for case/inquiry selector search
  const [selectorSearch, setSelectorSearch] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorFilter, setSelectorFilter] = useState<'all' | 'cases' | 'inquiries'>('all');
  const selectorRef = useRef<HTMLDivElement>(null);

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build combined list of cases and inquiries for task assignment
  // Get the set of caseIds that belong to ANY inquiry (these should show under Inquiries, not Cases)
  const inquiryCaseIds = new Set(
    inquiries
      .filter((inq: Inquiry) => inq.caseId) // All inquiries with a case
      .map((inq: Inquiry) => inq.caseId)
  );

  const assignableItems = [
    // Real client cases - exclude cases linked to inquiries (those show under Inquiries tab)
    ...cases
      .filter((c: Case) => 
        c.caseType !== 'INQUIRY_PLACEHOLDER' && // Exclude legacy placeholder cases
        !inquiryCaseIds.has(c.id) // Exclude cases linked to inquiries
      )
      .map((c: Case) => ({
        id: c.id,
        type: 'case' as const,
        label: c.caseNumber,
        clientName: c.client ? `${c.client.firstName} ${c.client.lastName}`.trim() : 'Unknown Client',
        clientId: c.clientId,
        status: c.status,
        searchText: `${c.caseNumber} ${c.client?.firstName || ''} ${c.client?.lastName || ''}`.toLowerCase(),
      })),
    // Inquiries with linked cases AND users (PROSPECT users who created accounts)
    ...inquiries
      .filter((inq: Inquiry) => inq.caseId && inq.userId) // Only inquiries with linked case AND user
      .map((inq: Inquiry) => {
        // Find the linked case to get caseNumber
        const linkedCase = cases.find((c: Case) => c.id === inq.caseId);
        return {
          id: inq.caseId!, // Use the linked caseId for task assignment
          type: 'inquiry' as const,
          label: linkedCase?.caseNumber || `INQ-${inq.id.slice(0, 8)}`,
          clientName: `${inq.firstName} ${inq.lastName}`.trim(),
          clientId: inq.userId!,
          status: inq.status,
          inquiryId: inq.id,
          incidentType: inq.incidentType,
          searchText: `${linkedCase?.caseNumber || ''} ${inq.firstName} ${inq.lastName} ${inq.incidentType || ''}`.toLowerCase(),
        };
      }),
  ];

  // Filter assignable items based on search and filter
  const filteredAssignableItems = assignableItems.filter(item => {
    const matchesSearch = !selectorSearch || item.searchText.includes(selectorSearch.toLowerCase());
    const matchesFilter = selectorFilter === 'all' || 
      (selectorFilter === 'cases' && item.type === 'case') ||
      (selectorFilter === 'inquiries' && item.type === 'inquiry');
    return matchesSearch && matchesFilter;
  });

  // Get selected item display
  const selectedItem = assignableItems.find(item => item.id === selectedCaseId);

  // Get unique clients from cases
  const uniqueClients = cases.reduce((acc: any[], caseItem: any) => {
    const clientId = caseItem.client?.id;
    if (clientId && !acc.find(c => c.id === clientId)) {
      acc.push({
        id: clientId,
        name: `${caseItem.client?.firstName || ''} ${caseItem.client?.lastName || ''}`.trim(),
      });
    }
    return acc;
  }, []).sort((a: any, b: any) => a.name.localeCompare(b.name));

  // Filter cases based on selected client
  const filteredCases = clientFilter === 'ALL' 
    ? cases 
    : cases.filter((c: any) => c.client?.id === clientFilter);

  // Reset case filter when client changes and selected case is not in filtered list
  useEffect(() => {
    if (caseFilter !== 'ALL' && clientFilter !== 'ALL') {
      const caseStillValid = filteredCases.some((c: any) => c.id === caseFilter);
      if (!caseStillValid) {
        setCaseFilter('ALL');
      }
    }
  }, [clientFilter, filteredCases, caseFilter]);

  // Fetch all tasks from all cases
  useEffect(() => {
    const fetchAllTasks = async () => {
      if (cases.length === 0) return;

      const tasksPromises = cases.map(async (caseItem: any) => {
        try {
          const { data } = await getTasksByCase({
            variables: { caseId: caseItem.id },
          });

          const tasks = data?.listTasksByCase?.items || [];
          return tasks.map((task: any) => ({
            ...task,
            caseNumber: caseItem.caseNumber,
            clientName: `${caseItem.client?.firstName || ''} ${caseItem.client?.lastName || ''}`.trim(),
          }));
        } catch (error) {
          console.error(`Error fetching tasks for case ${caseItem.id}:`, error);
          return [];
        }
      });

      const tasksArrays = await Promise.all(tasksPromises);
      const flattenedTasks = tasksArrays.flat();
      setAllTasks(flattenedTasks);
    };

    fetchAllTasks();
  }, [cases, getTasksByCase]);

  const refetchTasks = async () => {
    if (cases.length === 0) return;

    console.log('Refetching tasks for', cases.length, 'cases...');

    const tasksPromises = cases.map(async (caseItem: any) => {
      try {
        const { data } = await getTasksByCase({
          variables: { caseId: caseItem.id },
        });

        const tasks = data?.listTasksByCase?.items || [];
        console.log(`Case ${caseItem.caseNumber}: ${tasks.length} tasks`);
        return tasks.map((task: any) => ({
          ...task,
          caseNumber: caseItem.caseNumber,
          clientName: `${caseItem.client?.firstName || ''} ${caseItem.client?.lastName || ''}`.trim(),
        }));
      } catch (error) {
        console.error(`Error fetching tasks for case ${caseItem.id}:`, error);
        return [];
      }
    });

    const tasksArrays = await Promise.all(tasksPromises);
    const flattenedTasks = tasksArrays.flat();
    console.log('Total tasks after refetch:', flattenedTasks.length);
    setAllTasks(flattenedTasks);
  };

  const handleCreateTask = async () => {
    console.log('=== START CREATE TASK ===');
    console.log('Selected Case ID:', selectedCaseId);
    console.log('Task Title:', taskTitle);
    console.log('Task Description:', taskDescription);
    console.log('Task Type:', taskType);
    console.log('Task Priority:', taskPriority);
    console.log('Due Date:', taskDueDate);
    
    if (!selectedCaseId || !taskTitle.trim() || !taskDescription.trim() || !taskDueDate) {
      alert('Please fill in all required fields');
      return;
    }

    // Find the selected item (case or inquiry)
    const selectedAssignableItem = assignableItems.find(item => item.id === selectedCaseId);
    console.log('Selected Item:', selectedAssignableItem);
    
    if (!selectedAssignableItem) {
      alert('Invalid case or inquiry selected');
      return;
    }

    try {
      console.log('Calling createTask mutation...');
      const result = await createTask({
        variables: {
          input: {
            caseId: selectedCaseId,
            clientId: selectedAssignableItem.clientId,
            title: taskTitle,
            description: taskDescription,
            taskType,
            priority: taskPriority,
            dueDate: taskDueDate,
            staffNotes: taskStaffNotes || undefined,
          },
        },
      });

      console.log('✓ Task created successfully:', result.data?.createTask);
      console.log('Task ID:', result.data?.createTask?.id);

      // Add a small delay to ensure backend has processed the task
      console.log('Waiting 500ms before refetch...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refetch tasks
      console.log('Starting refetch...');
      await refetchTasks();
      console.log('✓ Refetch complete');
      
      setShowCreateTaskModal(false);
      // Reset form
      setSelectedCaseId('');
      setTaskTitle('');
      setTaskDescription('');
      setTaskType('DOCUMENT_UPLOAD');
      setTaskPriority('MEDIUM');
      setTaskDueDate('');
      setTaskStaffNotes('');
      setSelectedTemplateId('');
      setSelectorSearch('');
      setSelectorFilter('all');
      alert('Task assigned successfully!');
      console.log('=== END CREATE TASK ===');
    } catch (error: any) {
      console.error('Error creating task:', error);
      alert('Error creating task: ' + error.message);
    }
  };

  const getTaskTypeIcon = (type: string) => {
    const baseClasses = "w-5 h-5";
    const icons: Record<string, JSX.Element> = {
      DOCUMENT_UPLOAD: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      APPOINTMENT: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      FORM_COMPLETION: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      COMMUNICATION: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      REVIEW: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      COMPLIANCE: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      OTHER: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
    };
    return icons[type] || icons.OTHER;
  };

  const getTaskStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success',
      VERIFIED: 'success',
      REJECTED: 'default',
      OVERDUE: 'default',
    };
    return variants[status] || 'default';
  };

  const getTaskPriorityBadgeVariant = (priority: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      LOW: 'secondary',
      MEDIUM: 'info',
      HIGH: 'warning',
      URGENT: 'default',
    };
    return variants[priority] || 'default';
  };

  const isTaskOverdue = (dueDate: string, status: string) => {
    if (status === 'COMPLETED' || status === 'VERIFIED') return false;
    return new Date(dueDate) < new Date();
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} days`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Filter tasks
  const filteredTasks = allTasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'ALL' || task.priority === priorityFilter;
    const matchesCase = caseFilter === 'ALL' || task.caseId === caseFilter;
    
    // Get the case to check client
    const taskCase = cases.find((c: any) => c.id === task.caseId);
    const matchesClient = clientFilter === 'ALL' || taskCase?.client?.id === clientFilter;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesCase && matchesClient;
  });

  // Calculate stats
  const stats = {
    total: allTasks.length,
    pending: allTasks.filter(t => t.status === 'PENDING').length,
    inProgress: allTasks.filter(t => t.status === 'IN_PROGRESS').length,
    completed: allTasks.filter(t => t.status === 'COMPLETED' || t.status === 'VERIFIED').length,
    overdue: allTasks.filter(t => isTaskOverdue(t.dueDate, t.status)).length,
  };

  if (userLoading || casesLoading || inquiriesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-aalaw-charcoal">Tasks Overview</h1>
              <Badge variant="info" className="text-sm">
                {filteredTasks.length} {filteredTasks.length === 1 ? 'Task' : 'Tasks'}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/tasks/templates')}
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                Manage Templates
              </Button>
              <Button
                onClick={() => setShowCreateTaskModal(true)}
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
                className="hover:bg-blue-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Assign New Task
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-600 mt-1">Total Tasks</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                <p className="text-sm text-gray-600 mt-1">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                <p className="text-sm text-gray-600 mt-1">In Progress</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                <p className="text-sm text-gray-600 mt-1">Completed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                <p className="text-sm text-gray-600 mt-1">Overdue</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
              >
                <option value="ALL">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
              <select
                value={clientFilter}
                onChange={(e) => {
                  setClientFilter(e.target.value);
                  // Reset case filter when client changes
                  setCaseFilter('ALL');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
              >
                <option value="ALL">All Clients</option>
                {uniqueClients.map((client: any) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <select
                value={caseFilter}
                onChange={(e) => setCaseFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
              >
                <option value="ALL">All Cases</option>
                {filteredCases.map((caseItem: any) => (
                  <option key={caseItem.id} value={caseItem.id}>
                    {caseItem.caseNumber} {clientFilter === 'ALL' && caseItem.client ? `- ${caseItem.client.firstName} ${caseItem.client.lastName}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No tasks found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery || statusFilter !== 'ALL' || priorityFilter !== 'ALL' || caseFilter !== 'ALL' || clientFilter !== 'ALL'
                    ? 'Try adjusting your filters'
                    : 'Get started by assigning a new task'}
                </p>
                <Button 
                  onClick={() => setShowCreateTaskModal(true)}
                  style={{ backgroundColor: '#3B82F6', color: 'white' }}
                  className="hover:bg-blue-700"
                >
                  Assign New Task
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => {
              const overdue = isTaskOverdue(task.dueDate, task.status);
              return (
                <Card
                  key={task.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    overdue && task.status !== 'COMPLETED' && task.status !== 'VERIFIED'
                      ? 'border-red-300 bg-red-50'
                      : task.status === 'VERIFIED'
                      ? 'border-green-300 bg-green-50'
                      : ''
                  }`}
                  onClick={() => {
                    setSelectedTask(task);
                    setShowTaskDetailModal(true);
                  }}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="text-gray-600 mt-1">{getTaskTypeIcon(task.taskType)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900">{task.title}</h4>
                            <p className="text-xs text-gray-600 mt-1">
                              {task.caseNumber} • {task.clientName}
                            </p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Badge variant={getTaskPriorityBadgeVariant(task.priority)}>
                              {task.priority}
                            </Badge>
                            <Badge variant={getTaskStatusBadgeVariant(task.status)}>
                              {task.status}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className={overdue && task.status !== 'COMPLETED' && task.status !== 'VERIFIED' ? 'text-red-600 font-semibold' : ''}>
                            {getDaysUntilDue(task.dueDate)}
                          </span>
                          <span>•</span>
                          <span>Assigned by {task.assignedByName || 'Staff'}</span>
                          {task.completedAt && (
                            <>
                              <span>•</span>
                              <span className="text-green-600">✓ Completed {formatDate(task.completedAt)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Task Modal - Similar to CaseDetail but with case selector */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign New Task</h3>
            
            <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Case or Inquiry <span className="text-red-500">*</span>
                </label>
                
                {/* Searchable Case/Inquiry Selector */}
                <div ref={selectorRef} className="relative">
                  {/* Selected value display / Search input */}
                  <div
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-aalaw-red focus-within:border-transparent cursor-pointer bg-white"
                    onClick={() => setSelectorOpen(true)}
                  >
                    {selectedItem ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            selectedItem.type === 'inquiry' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {selectedItem.type === 'inquiry' ? 'Inquiry' : 'Case'}
                          </span>
                          <span className="font-medium text-gray-900">{selectedItem.label}</span>
                          <span className="text-gray-500">-</span>
                          <span className="text-gray-700">{selectedItem.clientName}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCaseId('');
                            setSelectorSearch('');
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-500">Click to select a case or inquiry...</span>
                    )}
                  </div>

                  {/* Dropdown */}
                  {selectorOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {/* Search input */}
                      <div className="p-3 border-b border-gray-200">
                        <input
                          type="text"
                          value={selectorSearch}
                          onChange={(e) => setSelectorSearch(e.target.value)}
                          placeholder="Search by name, case number, or incident type..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent text-sm"
                          autoFocus
                        />
                      </div>

                      {/* Filter tabs */}
                      <div className="flex border-b border-gray-200">
                        <button
                          type="button"
                          onClick={() => setSelectorFilter('all')}
                          className={`flex-1 px-4 py-2 text-sm font-medium ${
                            selectorFilter === 'all'
                              ? 'text-aalaw-red border-b-2 border-aalaw-red bg-red-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          All ({assignableItems.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectorFilter('cases')}
                          className={`flex-1 px-4 py-2 text-sm font-medium ${
                            selectorFilter === 'cases'
                              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          Cases ({assignableItems.filter(i => i.type === 'case').length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectorFilter('inquiries')}
                          className={`flex-1 px-4 py-2 text-sm font-medium ${
                            selectorFilter === 'inquiries'
                              ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          Inquiries ({assignableItems.filter(i => i.type === 'inquiry').length})
                        </button>
                      </div>

                      {/* Options list */}
                      <div className="max-h-60 overflow-y-auto">
                        {filteredAssignableItems.length === 0 ? (
                          <div className="px-4 py-8 text-center text-gray-500">
                            <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm">No matching cases or inquiries found</p>
                          </div>
                        ) : (
                          filteredAssignableItems.map((item) => (
                            <button
                              key={`${item.type}-${item.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedCaseId(item.id);
                                setSelectorOpen(false);
                                setSelectorSearch('');
                              }}
                              className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                                selectedCaseId === item.id ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-0.5 ${
                                  item.type === 'inquiry' 
                                    ? 'bg-purple-100 text-purple-800' 
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {item.type === 'inquiry' ? 'Inquiry' : 'Case'}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{item.label}</span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-gray-700">{item.clientName}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      item.status === 'ACTIVE' || item.status === 'NEW' || item.status === 'CONTACTED'
                                        ? 'bg-green-100 text-green-700'
                                        : item.status === 'QUALIFIED' || item.status === 'PENDING'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {item.status}
                                    </span>
                                    {item.type === 'inquiry' && 'incidentType' in item && item.incidentType && (
                                      <span className="text-xs text-gray-500">{item.incidentType}</span>
                                    )}
                                  </div>
                                </div>
                                {selectedCaseId === item.id && (
                                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedTemplateId === '' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Use Template (Optional)
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => {
                      const templateId = e.target.value;
                      setSelectedTemplateId(templateId);
                      
                      if (templateId) {
                        const template = templates.find((t: any) => t.id === templateId);
                        if (template) {
                          setTaskTitle(template.title);
                          setTaskDescription(template.description);
                          setTaskType(template.taskType);
                          setTaskPriority(template.priority);
                          
                          const dueDate = new Date();
                          dueDate.setDate(dueDate.getDate() + template.defaultDueDays);
                          setTaskDueDate(dueDate.toISOString().split('T')[0]);
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Create Custom Task --</option>
                    {templates.map((template: any) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="e.g., Upload Medical Records"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  placeholder="Provide clear instructions for the client..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="DOCUMENT_UPLOAD">Document Upload</option>
                    <option value="APPOINTMENT">Appointment</option>
                    <option value="FORM_COMPLETION">Form Completion</option>
                    <option value="COMMUNICATION">Communication</option>
                    <option value="REVIEW">Review</option>
                    <option value="COMPLIANCE">Compliance</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={taskPriority}
                    onChange={(e) => setTaskPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staff Notes (Internal Only)
                </label>
                <textarea
                  value={taskStaffNotes}
                  onChange={(e) => setTaskStaffNotes(e.target.value)}
                  placeholder="Internal notes not visible to client..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCreateTaskModal(false);
                  setSelectedCaseId('');
                  setTaskTitle('');
                  setTaskDescription('');
                  setTaskType('DOCUMENT_UPLOAD');
                  setTaskPriority('MEDIUM');
                  setTaskDueDate('');
                  setTaskStaffNotes('');
                  setSelectedTemplateId('');
                  setSelectorSearch('');
                  setSelectorFilter('all');
                }}
              >
                Cancel
              </Button>
              <Button
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
                className="flex-1 hover:bg-blue-700"
                onClick={handleCreateTask}
              >
                Assign Task
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={showTaskDetailModal}
        onClose={() => {
          setShowTaskDetailModal(false);
          setSelectedTask(null);
        }}
        onTaskUpdated={refetchTasks}
      />
    </div>
  );
}
